import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '../../../config/queryKeys'
import {
  getPlayerSeasonStats,
  PlayerSeasonStatRow,
} from '../../../services/player/playerSeasonStats'

export interface UsePlayerSeasonStatsArgs {
  playerId?: string
  sportId?: string
  genericSportId?: string | null
  isSkater?: boolean
  isGoalie?: boolean
  enabled?: boolean
}

export interface UsePlayerSeasonStatsResult {
  playerStats: PlayerSeasonStatRow[]
  goalieStats: PlayerSeasonStatRow[]
  isLoading: boolean
  error: Error | null
  refetch: () => void
}

const EMPTY: PlayerSeasonStatRow[] = []

/**
 * React Query wrapper that fetches a player's season stats for the selected
 * sport. Runs a skater query and/or a goalie query depending on the flags, each
 * keyed by (player, sport, generic sport, goalie) so switching player or sport
 * refetches and caches independently.
 */
export const usePlayerSeasonStats = ({
  playerId,
  sportId,
  genericSportId = null,
  isSkater = false,
  isGoalie = false,
  enabled = true,
}: UsePlayerSeasonStatsArgs): UsePlayerSeasonStatsResult => {
  const baseEnabled = enabled && Boolean(playerId) && sportId != null

  const skater = useQuery({
    queryKey: queryKeys.playerSeasonStats.byPlayerSport(
      playerId,
      sportId,
      genericSportId,
      false
    ),
    queryFn: () =>
      getPlayerSeasonStats({
        playerId: playerId as string,
        sportId,
        genericSportId,
        goalie: false,
      }),
    enabled: baseEnabled && isSkater,
    staleTime: 1000 * 60 * 2,
  })

  const goalie = useQuery({
    queryKey: queryKeys.playerSeasonStats.byPlayerSport(
      playerId,
      sportId,
      genericSportId,
      true
    ),
    queryFn: () =>
      getPlayerSeasonStats({
        playerId: playerId as string,
        sportId,
        genericSportId,
        goalie: true,
      }),
    enabled: baseEnabled && isGoalie,
    staleTime: 1000 * 60 * 2,
  })

  const refetch = () => {
    if (baseEnabled && isSkater) {
      skater.refetch()
    }
    if (baseEnabled && isGoalie) {
      goalie.refetch()
    }
  }

  return {
    playerStats: skater.data ?? EMPTY,
    goalieStats: goalie.data ?? EMPTY,
    isLoading:
      (baseEnabled && isSkater && skater.isLoading) ||
      (baseEnabled && isGoalie && goalie.isLoading),
    error: skater.error ?? goalie.error ?? null,
    refetch,
  }
}

export default usePlayerSeasonStats
